import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Loader } from '@mantine/core'

import { GET_ADMIN_CARRIER } from 'src/graphql/carriers.graphql'

import CarrierDetail, { type CarrierEditableSection } from './CarrierDetail'

export interface CarrierDetailPanelProps {
  carrierId: string
  /** Bumped by the caller after a change next door, to re-read the carrier. */
  reloadKey?: number
}

/**
 * Fetches one carrier and hands it to {@link CarrierDetail}. Keeping the query
 * here leaves the view itself presentational.
 */
const CarrierDetailPanel = ({
  carrierId,
  reloadKey = 0,
}: CarrierDetailPanelProps) => {
  // One slot for both sections, so opening one form closes the other: two
  // forms saving against the same carrier would each re-read and overwrite
  // the other's view.
  const [editing, setEditing] = useState<CarrierEditableSection | null>(null)

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_CARRIER, {
    variables: { carrierId },
    fetchPolicy: 'cache-and-network',
  })

  // The contact count above the table is this query's, and archiving a contact
  // changes it without touching anything this query asked for. Nothing in its
  // own variables moved, so it has to be told.
  useEffect(() => {
    if (reloadKey > 0) refetch()
  }, [reloadKey, refetch])

  const carrier = data?.carrierByCarrierId

  if (loading && !carrier) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-doubleNickel-gray-500">
        <Loader size="sm" />
        Loading carrier
      </div>
    )
  }

  // Only when there is nothing to show. With cache-and-network a failed
  // background refetch would otherwise replace a half-typed form with an error.
  if (error && !carrier) {
    return (
      <div className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-25 p-4 text-sm text-doubleNickel-error-700">
        {error.message}
      </div>
    )
  }

  if (!carrier) {
    return (
      <div className="rounded-lg border border-doubleNickel-gray-200 p-6 text-sm text-doubleNickel-gray-600">
        No active carrier with this id. It may have been archived.
      </div>
    )
  }

  return (
    <CarrierDetail
      carrier={carrier}
      onEdit={() => setEditing('details')}
      onEditRouting={() => setEditing('routing')}
      editingSection={editing}
      onCancelEdit={() => setEditing(null)}
      onSaved={refetch}
    />
  )
}

export default CarrierDetailPanel
